import type { ConversationSalesContext } from '@/services/conversationsApi'

/**
 * Un producto está "confirmado" cuando el embudo ya lo tiene en el borrador de orden
 * o como producto seleccionado en el checkpoint. `lastMentionedProductName` es solo
 * una mención y NO basta (regla core: checkout_without_product = 0).
 * La UI solo lee; la fuente de verdad sigue siendo activeFunnelSnapshot en backend.
 */
export function hasConfirmedProduct(
  salesContext?: Pick<ConversationSalesContext, 'activeOrderDraft' | 'product'> | null
): boolean {
  if (!salesContext) return false
  const draftItems = salesContext.activeOrderDraft?.items
  if (Array.isArray(draftItems) && draftItems.length > 0) return true
  return Boolean(salesContext.product?.id)
}

export interface IncomingMessageScrollInput {
  previous: { id?: string; count: number } | null
  currentId?: string
  currentCount: number
  lastDirection?: 'in' | 'out'
  isAtBottom: boolean
}

export type IncomingMessageScrollDecision =
  | { action: 'none' }
  | { action: 'scroll' }
  | { action: 'count'; added: number }

/**
 * Decide qué hacer cuando cambia la lista de mensajes:
 * - Primera carga / cambio de chat / sin mensajes nuevos: nada.
 * - Mensaje propio: scroll al fondo.
 * - Mensaje entrante con el usuario leyendo historial: acumular contador.
 * - Mensaje entrante con el usuario al fondo: lo maneja useScrollToBottom.
 */
export function decideIncomingMessageScroll(
  input: IncomingMessageScrollInput
): IncomingMessageScrollDecision {
  const { previous, currentId, currentCount, lastDirection, isAtBottom } = input
  if (!previous || previous.id !== currentId || currentCount <= previous.count) {
    return { action: 'none' }
  }
  if (lastDirection === 'out') return { action: 'scroll' }
  if (!isAtBottom) return { action: 'count', added: currentCount - previous.count }
  return { action: 'none' }
}

export interface GroupableMessage {
  direction: 'in' | 'out'
  source?: string
  timestamp: Date | string
}

const GROUP_WINDOW_MS = 2 * 60 * 1000

function senderKey(msg: GroupableMessage): string {
  if (msg.direction === 'in') return 'customer'
  return msg.source && msg.source !== 'human' ? 'bot' : 'human'
}

/**
 * Indica si un mensaje inicia un grupo visual: cambia el emisor o pasaron >2 min
 * desde el anterior. Solo el primero del grupo muestra la "colita" de burbuja.
 */
export function isFirstInMessageGroup(
  previous: GroupableMessage | null | undefined,
  current: GroupableMessage
): boolean {
  if (!previous) return true
  if (senderKey(previous) !== senderKey(current)) return true
  const gap = new Date(current.timestamp).getTime() - new Date(previous.timestamp).getTime()
  return !Number.isFinite(gap) || gap > GROUP_WINDOW_MS || gap < 0
}
